import { styled } from '@mui/material/styles';
import { Popover, Box } from '@mui/material';
import { COLOR } from '@src/styles/color';
import { MOBILE_BREAKPOINT } from '@src/styles/config';

const StyledNotificationPanel = styled(Popover)`
  .MuiPaper-root {
    background: ${COLOR.white};
    border: 0.5px solid ${COLOR.indigo[16]};
    border-radius: 16px;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
    width: 375px;
    max-height: ${({ bottom }) => `calc(100vh - ${bottom}px)`};
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
    overflow: clip;
    margin-top: 8px;
    right: 16px !important;
    left: auto !important;
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}) {
    .MuiPaper-root {
      width: 100%;
      max-width: 100%;
      max-height: 100dvh;
      height: 100dvh;
      border-radius: 0;
      border: none;
      box-shadow: none;
      margin-top: 0;
      top: 0 !important;
      left: 0 !important;
    }
  }
`;

const StyledHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;

  .header-title {
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: -0.4px;
    color: ${COLOR.darkGray};
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 144px;
    justify-content: flex-end;
  }

  .action-btn {
    display: flex;
    align-items: center;
    padding: 8px;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }
`;

const StyledTabs = styled(Box)`
  background: ${COLOR.charcoal};
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 12px;
  display: flex;
  align-items: center;
  padding: 4px;
  position: sticky;
  top: 0;
  z-index: 1;

  .tab-item {
    flex: 1 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 40px;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    box-sizing: border-box;

    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.4px;
    color: ${COLOR.label2[64]};

    &.active {
      background: ${COLOR.white};
      box-shadow: 0px 2px 16px rgba(0, 0, 0, 0.08);
      color: ${COLOR.darkGray};
    }
  }

  .unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${COLOR.functionalPink.default};
    flex-shrink: 0;
  }
`;

const StyledNotificationList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 300px;
`;

const StyledNotificationItem = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: ${COLOR.indigo[4]};
  }

  .item-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 8px;
    width: 100%;
  }

  .item-title-container {
    display: flex;
    flex: 1 0 0;
    align-items: center;
    gap: 8px;
    position: relative;
  }

  .item-title {
    flex: 1 0 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: -0.4px;
    color: ${COLOR.black.default};
  }

  .pin-icon {
    position: absolute;
    left: 0;
    top: 2px;
    font-size: 20px;
    color: ${COLOR.indigo[64]};
  }

  .item-title.pinned {
    padding-left: 24px;
  }

  .navigate-icon {
    font-size: 20px;
    color: ${COLOR.label2[64]};
    flex-shrink: 0;
  }

  .unread-indicator {
    width: 8px;
    height: 8px;
    min-width: 8px;
    border-radius: 50%;
    background: ${COLOR.functionalPink.default};
    flex-shrink: 0;
    margin-top: 8px;
  }

  .item-description {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.4px;
    color: ${COLOR.label2[64]};
    width: 100%;
    white-space: pre-wrap;
  }

  .item-image {
    width: 100%;
    height: 164px;
    border-radius: 8px;
    object-fit: cover;
  }

  .item-date {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.4px;
    color: ${COLOR.label2[64]};
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}) {
    &:hover {
      background: ${(props) =>
        props.$unread ? COLOR.indigo[4] : 'transparent'};
    }
  }
`;

const StyledNotificationEmpty = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  min-height: 300px;

  .empty-image {
    width: 150px;
    height: 120px;
    object-fit: contain;
  }

  .empty-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.4px;
    color: ${COLOR.label2[64]};
  }
`;

const StyledDivider = styled(Box)`
  height: 0;
  width: 100%;
  border-bottom: 0.5px solid ${COLOR.indigo[16]};
`;

const StyledDetailContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .detail-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.4px;
    color: ${COLOR.darkGray};
  }

  .detail-body {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.4px;
    color: ${COLOR.darkGray};
    white-space: pre-wrap;
  }
`;

export {
  StyledNotificationPanel,
  StyledHeader,
  StyledTabs,
  StyledNotificationList,
  StyledNotificationEmpty,
  StyledNotificationItem,
  StyledDivider,
  StyledDetailContent,
};
